import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PlusCircle, Trash2, Edit2, Package, Truck, ImagePlus, ArrowLeft, ShoppingBag, Wand2, Loader2, Star } from "lucide-react";

const CATEGORIES = [
  { slug: "heating", label: "Chauffage" },
  { slug: "pump", label: "Pompe" },
  { slug: "electronics", label: "Électronique" },
  { slug: "water", label: "Traitement eau" },
  { slug: "filter", label: "Filtration" },
  { slug: "other", label: "Autres" },
];

const TABS = [
  { key: "kits", label: "Kits", Icon: Package },
  { key: "shipping", label: "Livraison", Icon: Truck },
  { key: "orders", label: "Commandes", Icon: ShoppingBag },
  { key: "reviews", label: "Avis", Icon: Star },
];

const emptyKit = () => ({
  name: "", short_description: "", long_description_markdown: "",
  category: "electronics", spa_brand: "", price_cents: 4990,
  difficulty: "medium", estimated_time_min: 30,
  included_parts: [], required_tools: [],
  linked_tutorial_slug: "", weight_grams: 500, active: true,
});

const emptyTier = () => ({
  name: "", amount_cents: 590, currency: "eur",
  allowed_countries: ["FR"],
  delivery_min_days: 3, delivery_max_days: 5,
  description: "", active: true, sort_order: 100,
});

export default function AdminKits() {
  const [tab, setTab] = useState("kits");
  const [kits, setKits] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [orders, setOrders] = useState({ items: [], kpi: null });
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editKit, setEditKit] = useState(null);
  const [editTier, setEditTier] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [aiJobs, setAiJobs] = useState({});
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const photoInputRef = useRef(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/kits").catch(() => ({ data: [] })),
      api.get("/admin/kits/shipping-tiers").catch(() => ({ data: [] })),
      api.get("/admin/kits/orders").catch(() => ({ data: { items: [], kpi: null } })),
      api.get("/admin/kits/reviews").catch(() => ({ data: { items: [] } })),
    ])
      .then(([k, t, o, rv]) => {
        setKits(k.data || []);
        setTiers(t.data || []);
        setOrders(o.data || { items: [], kpi: null });
        setReviews(rv.data?.items || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  // ---- Kit CRUD ----
  const saveKit = async () => {
    try {
      const payload = { ...editKit, spa_brand: editKit.spa_brand || null, linked_tutorial_slug: editKit.linked_tutorial_slug || null };
      // Parse text tools if entered as newline-separated string
      if (typeof payload.required_tools === "string") {
        payload.required_tools = payload.required_tools.split("\n").map((s) => s.trim()).filter(Boolean);
      }
      // Parse included_parts if it's a string (JSON)
      if (typeof payload.included_parts === "string") {
        try { payload.included_parts = JSON.parse(payload.included_parts); } catch { toast.error("Contenu du kit : JSON invalide"); return; }
      }
      if (editKit.id) {
        await api.patch(`/admin/kits/${editKit.id}`, payload);
        toast.success("Kit mis à jour");
      } else {
        await api.post("/admin/kits", payload);
        toast.success("Kit créé");
      }
      setEditKit(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const deleteKit = async (id) => {
    if (!window.confirm("Supprimer ce kit ? Les commandes existantes ne seront pas affectées.")) return;
    try {
      await api.delete(`/admin/kits/${id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const uploadKitPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editKit?.id) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/admin/kits/${editKit.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditKit(r.data);
      toast.success("Photo ajoutée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload échoué");
    }
    if (photoInputRef.current) photoInputRef.current.value = "";
  };

  // ---- Photos IA ----
  const pollAiJob = (kitId) => {
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/kits/${kitId}/generate-photos/status`);
        setAiJobs((j) => ({ ...j, [kitId]: data }));
        if (data.status === "done" || data.status === "error") {
          clearInterval(timer);
          if (data.status === "done") {
            toast.success(`${data.done} photo(s) IA générée(s) !`);
            load();
          } else {
            toast.error(data.error || "Génération échouée");
          }
        }
      } catch {
        clearInterval(timer);
      }
    }, 3000);
  };

  const generateAiPhotos = async (kit) => {
    try {
      await api.post(`/admin/kits/${kit.id}/generate-photos`);
      setAiJobs((j) => ({ ...j, [kit.id]: { status: "running", done: 0, total: 4 } }));
      toast.info("Génération des 4 photos IA lancée (1 à 2 minutes)…");
      pollAiJob(kit.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  // ---- Tier CRUD ----
  const saveTier = async () => {
    try {
      const payload = { ...editTier };
      if (typeof payload.allowed_countries === "string") {
        payload.allowed_countries = payload.allowed_countries.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);
      }
      if (editTier.id) {
        await api.patch(`/admin/kits/shipping-tiers/${editTier.id}`, payload);
        toast.success("Palier mis à jour");
      } else {
        await api.post("/admin/kits/shipping-tiers", payload);
        toast.success("Palier créé");
      }
      setEditTier(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const deleteTier = async (id) => {
    if (!window.confirm("Supprimer ce palier ?")) return;
    try {
      await api.delete(`/admin/kits/shipping-tiers/${id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  // ---- Reviews ----
  const deleteReview = async (id) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    try {
      await api.delete(`/admin/kits/reviews/${id}`);
      toast.success("Avis supprimé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  // ---- Order fulfillment ----
  const updateOrder = async (id, payload) => {
    try {
      await api.patch(`/admin/kits/orders/${id}`, payload);
      toast.success("Commande mise à jour");
      setOrderDetail(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-10" data-testid="admin-kits-page">
      <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> Admin dashboard
      </Link>

      <div className="eyebrow text-secondary">Admin</div>
      <h1 className="mt-2 font-display text-3xl lg:text-4xl font-black tracking-tight">Kits de réparation</h1>

      {/* Tabs */}
      <div className="mt-8 flex gap-2 border-b">
        {TABS.map((t) => {
          const Icon = t.Icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 -mb-px border-b-2 text-sm font-semibold transition-colors flex items-center gap-2 ${
                tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`admin-kits-tab-${t.key}`}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* --- KITS --- */}
      {tab === "kits" && (
        <div className="mt-6" data-testid="admin-kits-list">
          <div className="flex justify-between items-center mb-4">
            <p className="text-xs text-muted-foreground">{kits.length} kit(s)</p>
            <Button onClick={() => setEditKit(emptyKit())} className="rounded-full" data-testid="btn-new-kit">
              <PlusCircle className="h-4 w-4 mr-2" /> Nouveau kit
            </Button>
          </div>
          {loading ? (<p className="text-sm text-muted-foreground">Chargement…</p>) : kits.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun kit — créez-en un !</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {kits.map((k) => (
                <Card key={k.id} className="p-4" data-testid={`admin-kit-${k.slug}`}>
                  <div className="flex gap-3">
                    <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-primary/10 via-secondary/20 to-slate-900 overflow-hidden shrink-0">
                      {k.cover_photo_url && <img src={`${backend}${k.cover_photo_url}`} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 flex-wrap text-[10px]">
                        <Badge variant={k.active ? "default" : "outline"} className="rounded-full">{k.active ? "Actif" : "Inactif"}</Badge>
                        <Badge variant="secondary" className="rounded-full">{k.category}</Badge>
                        {k.spa_brand && <Badge variant="outline" className="rounded-full">{k.spa_brand}</Badge>}
                      </div>
                      <h3 className="mt-1 font-bold text-sm truncate">{k.name}</h3>
                      <p className="text-xs text-muted-foreground line-clamp-1">{k.short_description}</p>
                      <div className="mt-2 flex items-center justify-between text-xs">
                        <span className="font-display font-black text-primary">{k.price_display}</span>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-violet-600"
                            onClick={() => generateAiPhotos(k)}
                            disabled={aiJobs[k.id]?.status === "running"}
                            title="Générer 4 photos IA (Gemini)"
                            data-testid={`ai-photos-kit-${k.slug}`}
                          >
                            {aiJobs[k.id]?.status === "running" ? (
                              <span className="flex items-center gap-1 text-[10px] font-mono">
                                <Loader2 className="h-3 w-3 animate-spin" /> {aiJobs[k.id].done}/{aiJobs[k.id].total}
                              </span>
                            ) : (
                              <Wand2 className="h-3 w-3" />
                            )}
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditKit(k)} data-testid={`edit-kit-${k.slug}`}>
                            <Edit2 className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={() => deleteKit(k.id)} data-testid={`delete-kit-${k.slug}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* --- SHIPPING TIERS --- */}
      {tab === "shipping" && (
        <div className="mt-6" data-testid="admin-tiers-list">
          <div className="flex justify-between items-center mb-4">
            <p className="text-xs text-muted-foreground">{tiers.length} palier(s)</p>
            <Button onClick={() => setEditTier(emptyTier())} className="rounded-full" data-testid="btn-new-tier">
              <PlusCircle className="h-4 w-4 mr-2" /> Nouveau palier
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tiers.map((t) => (
              <Card key={t.id} className="p-4" data-testid={`admin-tier-${t.id}`}>
                <div className="flex justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-xs">
                      <Badge variant={t.active ? "default" : "outline"} className="rounded-full">{t.active ? "Actif" : "Inactif"}</Badge>
                      <span className="font-mono text-xs">#{t.sort_order}</span>
                    </div>
                    <h4 className="font-bold mt-1">{t.name}</h4>
                    <p className="text-xs text-muted-foreground">{t.description}</p>
                    <p className="text-xs mt-1"><strong>{t.amount_display}</strong> · {t.delivery_min_days}-{t.delivery_max_days} j · {(t.allowed_countries || []).join(", ")}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditTier(t)}><Edit2 className="h-3 w-3" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={() => deleteTier(t.id)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* --- ORDERS --- */}
      {tab === "orders" && (
        <div className="mt-6" data-testid="admin-orders-list">
          {orders.kpi && (
            <div className="grid grid-cols-2 gap-3 mb-6">
              <Card className="p-4"><p className="text-xs text-muted-foreground">Commandes payées</p><p className="mt-1 font-display text-2xl font-black">{orders.kpi.paid_orders}</p></Card>
              <Card className="p-4"><p className="text-xs text-muted-foreground">Chiffre d'affaires</p><p className="mt-1 font-display text-2xl font-black text-emerald-600">{orders.kpi.paid_revenue_display}</p></Card>
            </div>
          )}
          {orders.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune commande pour le moment.</p>
          ) : (
            <div className="space-y-2">
              {orders.items.map((o) => (
                <Card key={o.id} className="p-4 cursor-pointer hover:border-primary/40" onClick={() => setOrderDetail(o)} data-testid={`order-${o.id}`}>
                  <div className="flex justify-between gap-4 items-center">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-xs">
                        <Badge className={`rounded-full ${o.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" : o.payment_status === "initiated" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"} border-0`}>
                          {o.payment_status}
                        </Badge>
                        <Badge variant="outline" className="rounded-full">{o.fulfillment_status}</Badge>
                        <span className="text-muted-foreground text-[10px]">{new Date(o.created_at).toLocaleString("fr-FR")}</span>
                      </div>
                      <p className="mt-1 font-semibold truncate">{o.kit_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{o.customer_email} · qty {o.quantity}</p>
                    </div>
                    <span className="font-bold text-primary shrink-0">{o.total_display}</span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* --- REVIEWS --- */}
      {tab === "reviews" && (
        <div className="mt-6" data-testid="admin-reviews-list">
          {reviews.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun avis pour le moment.</p>
          ) : (
            <div className="space-y-2">
              {reviews.map((rv) => (
                <Card key={rv.id} className="p-4" data-testid={`admin-review-${rv.id}`}>
                  <div className="flex justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap text-xs">
                        <span className="inline-flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Star key={i} className={`h-3.5 w-3.5 ${i <= rv.rating ? "fill-amber-400 text-amber-400" : "text-black/15"}`} />
                          ))}
                        </span>
                        <span className="font-semibold">{rv.author_name}</span>
                        <Badge variant="outline" className="rounded-full">{rv.kit_name}</Badge>
                        <span className="text-muted-foreground text-[10px]">{rv.created_at && new Date(rv.created_at).toLocaleDateString("fr-FR")}</span>
                      </div>
                      {rv.comment && <p className="mt-1.5 text-sm text-muted-foreground">{rv.comment}</p>}
                    </div>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 shrink-0" onClick={() => deleteReview(rv.id)} data-testid={`delete-review-${rv.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ==== KIT EDIT DIALOG ==== */}
      <Dialog open={!!editKit} onOpenChange={(o) => !o && setEditKit(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editKit?.id ? "Modifier le kit" : "Nouveau kit"}</DialogTitle></DialogHeader>
          {editKit && (
            <div className="space-y-3">
              <div><Label>Nom *</Label><Input value={editKit.name} onChange={(e) => setEditKit({ ...editKit, name: e.target.value })} data-testid="kit-input-name" /></div>
              <div><Label>Description courte</Label><Input value={editKit.short_description} onChange={(e) => setEditKit({ ...editKit, short_description: e.target.value })} maxLength={400} /></div>
              <div><Label>Description longue (Markdown)</Label><Textarea rows={6} value={editKit.long_description_markdown} onChange={(e) => setEditKit({ ...editKit, long_description_markdown: e.target.value })} className="font-mono text-xs" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Catégorie</Label>
                  <Select value={editKit.category} onValueChange={(v) => setEditKit({ ...editKit, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Marque spa (optionnel)</Label><Input value={editKit.spa_brand || ""} onChange={(e) => setEditKit({ ...editKit, spa_brand: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Prix (cts)</Label><Input type="number" value={editKit.price_cents} onChange={(e) => setEditKit({ ...editKit, price_cents: parseInt(e.target.value || 0) })} /></div>
                <div><Label>Difficulté</Label>
                  <Select value={editKit.difficulty} onValueChange={(v) => setEditKit({ ...editKit, difficulty: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Facile</SelectItem>
                      <SelectItem value="medium">Moyen</SelectItem>
                      <SelectItem value="hard">Expert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Temps (min)</Label><Input type="number" value={editKit.estimated_time_min} onChange={(e) => setEditKit({ ...editKit, estimated_time_min: parseInt(e.target.value || 0) })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Poids (g)</Label><Input type="number" value={editKit.weight_grams} onChange={(e) => setEditKit({ ...editKit, weight_grams: parseInt(e.target.value || 0) })} /></div>
                <div><Label>Slug tuto associé</Label><Input value={editKit.linked_tutorial_slug || ""} onChange={(e) => setEditKit({ ...editKit, linked_tutorial_slug: e.target.value })} placeholder="ex. remplacer-un-pressostat…" /></div>
              </div>
              <div><Label>Contenu du kit (JSON : [{"{ref,brand,qty,notes}"}])</Label>
                <Textarea rows={4} value={typeof editKit.included_parts === "string" ? editKit.included_parts : JSON.stringify(editKit.included_parts, null, 2)} onChange={(e) => setEditKit({ ...editKit, included_parts: e.target.value })} className="font-mono text-xs" />
              </div>
              <div><Label>Outils requis (1 par ligne)</Label>
                <Textarea rows={3} value={typeof editKit.required_tools === "string" ? editKit.required_tools : (editKit.required_tools || []).join("\n")} onChange={(e) => setEditKit({ ...editKit, required_tools: e.target.value })} />
              </div>
              <div className="flex items-center gap-2"><input type="checkbox" checked={editKit.active} onChange={(e) => setEditKit({ ...editKit, active: e.target.checked })} /> Kit actif (visible sur la boutique)</div>
              {editKit.id && (
                <div className="pt-3 border-t">
                  <Label>Photos</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {(editKit.gallery_photos || []).map((p, i) => (
                      <div key={p.path} className="aspect-square rounded bg-slate-100 overflow-hidden relative">
                        <img src={`${backend}${p.url}`} alt="" className="h-full w-full object-cover" />
                        {i === 0 && <Badge className="absolute top-1 left-1 text-[9px] bg-primary">Cover</Badge>}
                      </div>
                    ))}
                    <label className="aspect-square rounded border-2 border-dashed border-black/20 hover:border-primary/50 grid place-items-center cursor-pointer">
                      <ImagePlus className="h-6 w-6 text-muted-foreground" />
                      <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadKitPhoto} className="hidden" />
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditKit(null)}>Annuler</Button>
            <Button onClick={saveKit} data-testid="btn-save-kit">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==== TIER EDIT DIALOG ==== */}
      <Dialog open={!!editTier} onOpenChange={(o) => !o && setEditTier(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editTier?.id ? "Modifier le palier" : "Nouveau palier de livraison"}</DialogTitle></DialogHeader>
          {editTier && (
            <div className="space-y-3">
              <div><Label>Nom *</Label><Input value={editTier.name} onChange={(e) => setEditTier({ ...editTier, name: e.target.value })} /></div>
              <div><Label>Prix (centimes)</Label><Input type="number" value={editTier.amount_cents} onChange={(e) => setEditTier({ ...editTier, amount_cents: parseInt(e.target.value || 0) })} /></div>
              <div><Label>Pays autorisés (codes ISO 2 lettres, séparés par virgule)</Label>
                <Input value={typeof editTier.allowed_countries === "string" ? editTier.allowed_countries : (editTier.allowed_countries || []).join(", ")} onChange={(e) => setEditTier({ ...editTier, allowed_countries: e.target.value })} placeholder="FR, BE, LU…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Délai min (j)</Label><Input type="number" value={editTier.delivery_min_days} onChange={(e) => setEditTier({ ...editTier, delivery_min_days: parseInt(e.target.value || 1) })} /></div>
                <div><Label>Délai max (j)</Label><Input type="number" value={editTier.delivery_max_days} onChange={(e) => setEditTier({ ...editTier, delivery_max_days: parseInt(e.target.value || 1) })} /></div>
              </div>
              <div><Label>Description</Label><Input value={editTier.description || ""} onChange={(e) => setEditTier({ ...editTier, description: e.target.value })} /></div>
              <div><Label>Sort order</Label><Input type="number" value={editTier.sort_order} onChange={(e) => setEditTier({ ...editTier, sort_order: parseInt(e.target.value || 100) })} /></div>
              <div className="flex items-center gap-2"><input type="checkbox" checked={editTier.active} onChange={(e) => setEditTier({ ...editTier, active: e.target.checked })} /> Actif</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTier(null)}>Annuler</Button>
            <Button onClick={saveTier}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==== ORDER DETAIL DIALOG ==== */}
      <Dialog open={!!orderDetail} onOpenChange={(o) => !o && setOrderDetail(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Commande {orderDetail?.id?.slice(0, 8)}</DialogTitle></DialogHeader>
          {orderDetail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 p-3 rounded bg-slate-50">
                <div><strong>Kit :</strong> {orderDetail.kit_name}</div>
                <div><strong>Qté :</strong> {orderDetail.quantity}</div>
                <div><strong>Total :</strong> {orderDetail.total_display}</div>
                <div><strong>Statut paiement :</strong> {orderDetail.payment_status}</div>
              </div>
              <div><strong>Client :</strong> {orderDetail.customer_name} — {orderDetail.customer_email}</div>
              {orderDetail.shipping_address && (
                <div className="p-3 rounded bg-blue-50 text-xs">
                  <p className="font-semibold mb-1">Adresse de livraison</p>
                  <p>{orderDetail.shipping_address.name}</p>
                  <p>{orderDetail.shipping_address.line1} {orderDetail.shipping_address.line2}</p>
                  <p>{orderDetail.shipping_address.postal_code} {orderDetail.shipping_address.city}</p>
                  <p>{orderDetail.shipping_address.country}</p>
                </div>
              )}
              <div><Label>Statut fulfillment</Label>
                <Select value={orderDetail.fulfillment_status} onValueChange={(v) => setOrderDetail({ ...orderDetail, fulfillment_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="shipped">Expédié</SelectItem>
                    <SelectItem value="delivered">Livré</SelectItem>
                    <SelectItem value="cancelled">Annulé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>N° de suivi</Label><Input value={orderDetail.tracking_number || ""} onChange={(e) => setOrderDetail({ ...orderDetail, tracking_number: e.target.value })} placeholder="Ex. 6A12345678FR" /></div>
              <div><Label>Notes internes</Label><Textarea value={orderDetail.admin_notes || ""} onChange={(e) => setOrderDetail({ ...orderDetail, admin_notes: e.target.value })} rows={2} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderDetail(null)}>Fermer</Button>
            <Button onClick={() => updateOrder(orderDetail.id, {
              fulfillment_status: orderDetail.fulfillment_status,
              tracking_number: orderDetail.tracking_number,
              admin_notes: orderDetail.admin_notes,
            })} data-testid="btn-save-order">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
